/** @jsx jsx */
import { Fragment, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  Textarea,
  jsx,
} from 'theme-ui'
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { useCartFlyout } from '~/hooks/components/use-cart-flyout'
import ChevronIcon from '~/assets/images/icons/chevron.inline.svg'
import GiftIcon from '~/assets/images/icons/gift.inline.svg'
import PlusIcon from '~/assets/images/icons/plus.inline.svg'

const GiftMessageTrigger = ({
  isCartFlyoutOpen,
  isHidden,
  isOverlay,
  message,
  onClick,
}) => {
  const translate = useTranslate()

  if (message) {
    return (
      <Button
        variant="reset"
        onClick={onClick}
        py="12px"
        sx={{
          display: !isHidden ? 'block' : 'none',
          width: '100%',
        }}
      >
        <Flex mb={1} sx={{ justifyContent: 'space-between' }}>
          <Text as="strong" variant="body3" sx={{ fontWeight: 'bold' }}>
            {`${translate('cart.gift.added')}:`}
          </Text>
          <Text
            as="span"
            variant="body3"
            sx={{ color: 'brandPrimary', textDecoration: 'underline' }}
          >
            {translate('cart.gift.edit')}
          </Text>
        </Flex>
        <Text
          as="p"
          m={0}
          variant="description"
          color="greyDark"
          sx={{
            overflow: 'hidden',
            textAlign: 'left',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {message}
        </Text>
      </Button>
    )
  }

  return (
    <Button
      variant="reset"
      onClick={onClick}
      py="12px"
      sx={{
        alignItems: 'center',
        display: !isHidden ? 'flex' : 'none',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box as="span">
        <GiftIcon
          sx={{
            animation:
              isCartFlyoutOpen && isOverlay && '1s linear .3s forwards shake',
            color: 'brandTertiary',
            pointerEvents: 'none',
            position: 'relative',
            top: '2px',
            '@keyframes shake': {
              '0%': { transform: 'rotate(0deg)' },
              '20%': { transform: 'rotate(10deg) scale(1.5)' },
              '40%': { transform: 'rotate(-10deg) scale(1.5)' },
              '60%': { transform: 'rotate(10deg) scale(1.5)' },
              '80%': { transform: 'rotate(-10deg) scale(1.5)' },
              '100%': { transform: 'rotate(0deg) scale(1)' },
            },
          }}
        />
        <Text as="strong" variant="body2" ml={1} sx={{ fontWeight: 'bold' }}>
          {translate('cart.gift.toggle')}
        </Text>
      </Box>
      <PlusIcon
        sx={{ left: '-2px', pointerEvents: 'none', position: 'relative' }}
      />
    </Button>
  )
}

const GiftMessageInline = ({
  error,
  isLoading,
  isOpen,
  message,
  onChange,
  onClose,
  onSave,
}) => {
  const translate = useTranslate()

  return (
    <Box py={[2, 3]} sx={{ display: isOpen ? 'block' : 'none' }}>
      <GiftMessageForm
        buttonVariant="secondary"
        error={error}
        isLoading={isLoading}
        message={message}
        onChange={onChange}
        onSave={onSave}
      />
      <Box mt={2}>
        <Button
          onClick={onClose}
          variant="reset"
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '32px',
            width: 'calc(100% + 32px)',
          }}
        >
          <Text
            as="p"
            m={0}
            variant="body3"
            sx={{ color: 'brandPrimary', textDecoration: 'underline' }}
          >
            {translate('cart.gift.close')}
          </Text>
        </Button>
      </Box>
    </Box>
  )
}

const GiftMessageOverlay = ({
  error,
  isLoading,
  isOpen,
  message,
  onChange,
  onClose,
  onSave,
}) => {
  const translate = useTranslate()

  return (
    <Box
      bg="backgroundLight"
      px={[2, 3]}
      pb={[2, 3]}
      sx={{
        bottom: '0',
        boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.2)',
        left: '0',
        overflowY: 'scroll',
        position: 'absolute',
        right: '0',
        top: '0',
        transform: isOpen ? 'translateX(0)' : 'translateX(500px)',
        transition: 'transform 0.2s ease-in-out',
        zIndex: '201',
      }}
    >
      <Box
        mb={3}
        sx={{
          borderBottom: '1px solid',
          borderBottomColor: 'greyLight',
        }}
      >
        <Button
          onClick={onClose}
          variant="reset"
          ml={-2}
          pl={2}
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '64px',
            width: 'calc(100% + 32px)',
          }}
        >
          <ChevronIcon
            sx={{
              height: '10px',
              pointerEvents: 'none',
              position: 'relative',
              top: '1px',
              transform: 'rotate(90deg)',
              width: '16px',
            }}
          />
          <Text
            as="p"
            m={0}
            ml={1}
            variant="title5"
            sx={{ position: 'relative', top: '-1px' }}
          >
            {translate('cart.gift.back_cart')}
          </Text>
        </Button>
      </Box>
      <GiftMessageForm
        buttonVariant="primary"
        error={error}
        isLoading={isLoading}
        message={message}
        onChange={onChange}
        onSave={onSave}
      />
    </Box>
  )
}

const GiftMessageForm = ({
  buttonVariant,
  error,
  isLoading,
  message,
  onChange,
  onSave,
}) => {
  const buttonRef = useRef(null)
  const translate = useTranslate()
  const idSlug = Math.floor(100000 + Math.random() * 900000)

  return (
    <Box>
      <Box mb={3}>
        <Heading
          as="label"
          variant="body2"
          mb={1}
          htmlFor={`gift-message-value-${idSlug}`}
          sx={{ display: 'block' }}
        >
          {translate('cart.gift.form_heading')}
        </Heading>
        <Text as="p" variant="body3">
          {translate('cart.gift.form_description')}
        </Text>
      </Box>
      <Textarea
        mb={2}
        rows={5}
        value={message || ''}
        id={`gift-message-value-${idSlug}`}
        onClick={(e) =>
          buttonRef.current.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          })
        }
        onChange={onChange}
        sx={{
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'greyDark',
          borderRadius: 0,
          color: 'black',
          fontFamily: 'body',
          fontSize: '18px',
          letterSpacing: '0.5px',
          lineHeight: '30px',
          width: '100%',
        }}
      />
      {error && (
        <Text as="p" mb={2} variant="error">
          {error}
        </Text>
      )}
      <Button
        onClick={onSave}
        variant={buttonVariant}
        ref={buttonRef}
        type="button"
        sx={{ textAlign: 'center', width: '100%' }}
      >
        {isLoading ? (
          <Spinner
            size="15"
            color="inherit"
            sx={{
              '@media (prefers-reduced-motion)': {
                circle: {
                  animationIterationCount: 8,
                },
              },
            }}
          />
        ) : (
          translate('cart.gift.save')
        )}
      </Button>
    </Box>
  )
}

const GiftMessage = ({ giftNote, isOverlay = true }) => {
  const { modifyCart } = useCart()
  const { isCartFlyoutOpen } = useCartFlyout()
  const translate = useTranslate()
  const message = giftNote?.note

  const [updatedGiftMessage, setGiftMessage] = useState(message)
  const [apiError, setApiError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const saveGiftMessage = async (messageToSave) =>
    modifyCart({ attributes: { giftNoteAttributes: { note: messageToSave } } })

  const handleGiftMessageSave = async (e) => {
    if (isLoading) return

    try {
      setIsLoading(true)
      await saveGiftMessage(updatedGiftMessage)
      setIsExpanded(false)
      setIsLoading(false)
    } catch (error) {
      setApiError(translate('error.api.default'))
      console.error(error, { source: 'GiftMessage.update' })
    }
  }

  useEffect(() => {
    // Keeps multiple forms on the same page in sync (e.g. gift PDP and flyout).
    setGiftMessage(message)

    setIsLoading(false)
  }, [message])

  useEffect(() => {
    // Close the flyout overlay when it's clicked outside.
    if (isOverlay && !isCartFlyoutOpen) {
      setIsExpanded(false)
    }
  }, [isCartFlyoutOpen, isOverlay])

  const GiftMessageComponent = isOverlay
    ? GiftMessageOverlay
    : GiftMessageInline

  return (
    <Fragment>
      <GiftMessageTrigger
        isCartFlyoutOpen={isCartFlyoutOpen}
        isHidden={isExpanded && !isOverlay}
        isOverlay={isOverlay}
        message={message}
        onClick={() => setIsExpanded(true)}
      />
      <Box
        sx={{
          // Hides the form from tabbing unless open. Delays re-hiding on close
          // until overlay animation is complete.
          visibility: 'visible',
          animation: !isExpanded && '0s linear .2s forwards delayedHide',
          '@keyframes delayedHide': {
            to: {
              visibility: 'hidden',
            },
          },
        }}
      >
        <GiftMessageComponent
          error={apiError}
          isLoading={isLoading}
          isOpen={isExpanded}
          message={updatedGiftMessage}
          onChange={(e) => setGiftMessage(e.target.value)}
          onClose={() => setIsExpanded(false)}
          onSave={handleGiftMessageSave}
        />
      </Box>
    </Fragment>
  )
}

export default GiftMessage
